import type { ReactNode } from 'react'

import type { SettlementDay, SettlementInvoice, SettlementReport } from '../types'
import { settledAt, tradingPeriod } from './report-format'
import { ReportTable } from './report-table'
import type { ReportColumn, ReportRow } from './report-table'

/**
 * The cash columns both views end on: what physically crossed the counter, in
 * the currency it crossed in.
 *
 * Riel is never converted. The legacy settlement put a dollar figure under
 * "Cash (KHR)", which is how a drawer of riel reconciles on paper and not in
 * the safe.
 */
function cashColumns<T>(
  read: (row: T) => SettlementInvoice | SettlementDay['cash'],
): ReportColumn<T>[] {
  return [
    {
      id: 'receivedUsd',
      header: 'Cash in (USD)',
      format: 'usd',
      value: (row) => read(row).receivedUsd,
      hideWhenEmpty: true,
    },
    {
      id: 'receivedKhr',
      header: 'Cash in (KHR)',
      format: 'riel',
      value: (row) => read(row).receivedKhr,
      hideWhenEmpty: true,
    },
    {
      id: 'changeUsd',
      header: 'Change (USD)',
      format: 'usd',
      value: (row) => read(row).changeUsd,
      hideWhenEmpty: true,
    },
    {
      id: 'changeKhr',
      header: 'Change (KHR)',
      format: 'riel',
      value: (row) => read(row).changeKhr,
      hideWhenEmpty: true,
    },
  ]
}

/**
 * R16, daily view — the sheet owners actually read.
 *
 * Categories first, then the money, then how it was paid. The category columns
 * are ITEM net: after discounts, before VAT and delivery, which belong to the
 * order and not to any item. So they add up to After discount, which sits
 * right beside them for that check, and not to Net — the legacy sheet put them
 * next to the gross figure and left the reader to discover the gap.
 */
export function SettlementDailyTable({
  report,
  footer,
}: {
  report: SettlementReport
  footer?: ReactNode
}) {
  const columns: ReportColumn<SettlementDay>[] = [
    { id: 'date', header: 'Date', value: (row) => tradingPeriod(row.date) },
    { id: 'invoices', header: 'Invoices', format: 'whole', value: (row) => row.invoices },
    ...report.categories.map((category): ReportColumn<SettlementDay> => ({
      id: `category:${category}`,
      header: category,
      format: 'usd',
      value: (row) => row.byCategory[category] ?? 0,
    })),
    {
      id: 'afterDiscount',
      header: 'After discount',
      format: 'usd',
      value: (row) => row.afterDiscount,
    },
    { id: 'vat', header: 'VAT', format: 'usd', value: (row) => row.vat, hideWhenEmpty: true },
    {
      id: 'delivery',
      header: 'Delivery',
      format: 'usd',
      value: (row) => row.delivery,
      hideWhenEmpty: true,
    },
    { id: 'net', header: 'Net', format: 'usd', value: (row) => row.net },
    ...report.paymentMethods.map((method): ReportColumn<SettlementDay> => ({
      id: `method:${method.key}`,
      header: method.label,
      format: 'usd',
      value: (row) => row.byMethod[method.key] ?? 0,
    })),
    ...cashColumns<SettlementDay>((row) => row.cash),
    {
      id: 'refunds',
      header: 'Refunded',
      format: 'usd',
      value: (row) => row.refundAmount,
      hideWhenEmpty: true,
    },
  ]

  const rows: ReportRow<SettlementDay>[] = [
    ...report.days.map((day) => ({ id: day.date ?? 'day', data: day })),
    { id: 'total', data: report.total, label: 'Period total', tone: 'total' },
  ]

  return <ReportTable ariaLabel="Settlement by day" columns={columns} rows={rows} footer={footer} />
}

interface SettlementInvoiceTableProps {
  report: SettlementReport
  /** The page of invoices on screen. */
  invoices: SettlementInvoice[]
  footer?: ReactNode
}

/**
 * R16, per-invoice view — net, then each method, then the cash tender.
 *
 * It reconciles across the row: Net lands under exactly one method today
 * (an order takes one payment), and for cash, what came in less the change is
 * the net. The total row is the period's, carried over from the daily view so
 * the two can never disagree.
 */
export function SettlementInvoiceTable({ report, invoices, footer }: SettlementInvoiceTableProps) {
  type Line =
    SettlementInvoice | (SettlementDay['cash'] & { net: number; byMethod: Record<string, number> })

  const columns: ReportColumn<Line>[] = [
    { id: 'at', header: 'Time', value: (row) => ('at' in row ? settledAt(row.at) : null) },
    {
      id: 'invoice',
      header: 'Invoice',
      value: (row) => ('invoiceNumber' in row ? row.invoiceNumber : null),
      hideWhenEmpty: true,
    },
    {
      id: 'order',
      header: 'Order',
      value: (row) => ('orderNumber' in row ? row.orderNumber : null),
    },
    {
      id: 'branch',
      header: 'Outlet',
      value: (row) => ('branchName' in row ? row.branchName : null),
      visibleWhen: () => report.meta.branches.length > 1,
    },
    {
      id: 'cashier',
      header: 'Cashier',
      value: (row) => ('cashierName' in row ? row.cashierName : null),
      hideWhenEmpty: true,
    },
    { id: 'net', header: 'Net', format: 'usd', value: (row) => row.net },
    ...report.paymentMethods.map((method): ReportColumn<Line> => ({
      id: `method:${method.key}`,
      header: method.label,
      format: 'usd',
      value: (row) => row.byMethod[method.key] ?? 0,
    })),
    ...cashColumns<Line>((row) => row),
  ]

  const rows: ReportRow<Line>[] = [
    ...invoices.map((invoice, index) => ({
      id: `${invoice.orderNumber}:${invoice.at}:${index}`,
      data: invoice,
    })),
    {
      id: 'total',
      data: { ...report.total.cash, net: report.total.net, byMethod: report.total.byMethod },
      label: `Period total · ${report.total.invoices} ${report.total.invoices === 1 ? 'invoice' : 'invoices'}`,
      tone: 'total',
    },
  ]

  return (
    <ReportTable ariaLabel="Settlement by invoice" columns={columns} rows={rows} footer={footer} />
  )
}
